import React, { type ElementType, memo, useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import { graphql, useFragment } from 'react-relay';
import log from '@atlassian/jira-common-util-logging/src/log';
import { useIssueKey } from '@atlassian/jira-issue-context-service';
import legacyGetLayoutItemMap from '@atlassian/jira-issue-view-base/src/get-layout-item-map';
import { ItemBusyWrapperByIssueKey } from '@atlassian/jira-issue-view-common/src/component/item-busy-wrapper';
import { useIssueLayoutActions } from '@atlassian/jira-issue-view-layout';
import { UiModificationsRelayFieldDecorator } from '@atlassian/jira-issue-view-ui-modifications-relay-field-decorator';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { src_issueViewLayoutTemplatesLayoutItem_LayoutItemInternal$key as LayoutItemInternalFragment } from '@atlassian/jira-relay/src/__generated__/src_issueViewLayoutTemplatesLayoutItem_LayoutItemInternal.graphql';

import { RelayLayoutItemFragmentContainer, getLayoutItemMap } from './ui/relay-layout-items';
import { RelayLayoutItemErrorBoundary } from './ui/relay-layout-items/relay-layout-item-error-boundary';
import type {
	LayoutItemElementMapNonLiteral,
	LayoutItemProps,
	LayoutItemPropsWithPanel,
} from './ui/relay-layout-items/types';
import { isRelayDataReady } from './utils/is-relay-data-ready';

/**
 * Hook to return  the relevant relay-powered component given graphql type and optionally a jira field type
 *
 * @param graphqlTypename Search for a component in {@link LayoutItemMap} given the graphql {@code __typename}
 * @param jiraFieldType if the relevant {@code graphqlTypename} is for a {@code JiraIssueField}, then this should be the accompanying type according to jira (eg: used to distinguish assignee vs reporter vs custom user field for JiraSingleUserSelectField)
 * @returns A component from getLayoutItemMap Returns null if no relevant component is found.
 *          Will return the same answer even if the result should change between renders
 */
export const useRelayLayoutItemComponent = (
	graphqlTypename: string | null | undefined,
	jiraFieldType: string | null | undefined,
): ElementType<LayoutItemProps> | ElementType<LayoutItemPropsWithPanel> | null => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const layoutItemMap = getLayoutItemMap() as LayoutItemElementMapNonLiteral;
	const relayLayoutItemEntryOrWrapper = layoutItemMap[graphqlTypename || ''];
	const relayLayoutItemEntry =
		!!relayLayoutItemEntryOrWrapper && 'splitByJiraFieldType' in relayLayoutItemEntryOrWrapper
			? relayLayoutItemEntryOrWrapper.splitByJiraFieldType?.[jiraFieldType || '']
			: relayLayoutItemEntryOrWrapper;

	const RelayLayoutItemComponent = relayLayoutItemEntry?.item ?? null;
	return RelayLayoutItemComponent;
};

export type Props = {
	layoutItemFragment: LayoutItemInternalFragment | null;
	/** @deprecated should get all data from relay */
	type: string;
	/** @deprecated should get all data from relay */
	id: string;
	area: string;
	isLast?: boolean;
	/** @deprecated should get all data from relay */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	payload?: Record<any, any>;
};

/**
 * Component responsible for picking the correct component to mount in the issue-view layout-controlled areas.
 * Priority order of component selection:
 * 1. Relay powered component if provided by {@link useRelayLayoutItemComponent} based on the type of the field in its Relay data
 * 2. Falling back to finding matching legacy components via {@link getLayoutItemMap} which forms a circular dep back to {@code @atlassian/jira-issue-view}
 */
const LayoutItemInternal = ({
	id,
	type,
	area,
	isLast,
	payload = {},
	layoutItemFragment,
}: Props) => {
	const issueKey = useIssueKey();
	const data = useFragment<LayoutItemInternalFragment>(
		graphql`
			fragment src_issueViewLayoutTemplatesLayoutItem_LayoutItemInternal on JiraIssueField
			@argumentDefinitions(
				issueViewRelayCascadingSelectFlag: { type: "Boolean!", defaultValue: false }
				jsmProductsEntitlementsM2Flag: { type: "Boolean!", defaultValue: false }
				issueViewRelayGoalsFlag: { type: "Boolean!", defaultValue: false }
			) {
				__typename
				type
				fieldId
				...relayLayoutItems_issueViewLayoutTemplatesLayoutItem_RelayLayoutItemFragmentContainer
					@arguments(
						issueViewRelayCascadingSelectFlag: $issueViewRelayCascadingSelectFlag
						jsmProductsEntitlementsM2Flag: $jsmProductsEntitlementsM2Flag
						issueViewRelayGoalsFlag: $issueViewRelayGoalsFlag
					)
			}
		`,
		layoutItemFragment,
	);

	const [, { setIssueViewLayoutContextPanel }] = useIssueLayoutActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const handleOpenPanel = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'eventPayload' implicitly has an 'any' type.
		(eventPayload) => {
			const contextPanelData = {
				contextPanelType: data?.type ?? data?.__typename,
			};
			fireUIAnalytics(
				createAnalyticsEvent({}),
				'contextPanel viewed',
				'issueContextPanel',
				contextPanelData,
			);
			setIssueViewLayoutContextPanel(issueKey, {
				payload: eventPayload,
				title: eventPayload?.title,
				type,
				key: id,
			});
		},
		[createAnalyticsEvent, data, setIssueViewLayoutContextPanel, issueKey, type, id],
	);

	const RelayLayoutItemComponent = useRelayLayoutItemComponent(data?.__typename, data?.type);
	if (RelayLayoutItemComponent && isRelayDataReady(data)) {
		return (
			<RelayLayoutItemErrorBoundary itemName={data.type}>
				<UiModificationsRelayFieldDecorator fieldId={data.fieldId} issueKey={issueKey}>
					<RelayLayoutItemFragmentContainer
						fragmentKey={data}
						area={area}
						onOpenPanel={handleOpenPanel}
						Component={RelayLayoutItemComponent}
						isLast={isLast}
					/>
				</UiModificationsRelayFieldDecorator>
			</RelayLayoutItemErrorBoundary>
		);
	}

	const legacyLayoutItemMap = legacyGetLayoutItemMap();
	if (legacyLayoutItemMap[type]) {
		const LegacyLayoutItemComponent = legacyLayoutItemMap[type].item;
		return (
			<ItemBusyWrapperByIssueKey issueKey={issueKey} fieldId={id}>
				<LegacyLayoutItemComponent
					{...payload}
					key={id}
					fieldId={id}
					area={area}
					onOpenPanel={handleOpenPanel}
					isLast={isLast}
				/>
			</ItemBusyWrapperByIssueKey>
		);
	}

	log.safeErrorWithoutCustomerData(
		'issue.views.issue-base.context',
		`Could not find a matching component for "${id}" of type "${type}"`,
	);

	return null;
};

export const LayoutItem = memo<Props>(LayoutItemInternal, isEqual);
LayoutItem.displayName = 'LayoutItem';

export default LayoutItem;
